import { useState, useEffect } from 'react';
import {
  LayoutDashboard, Key, Link2, TrendingUp, BarChart3, FileText, FileBarChart,
  Bot, Eye, Target, Activity, ArrowUpRight,
  Share2, Megaphone, Video, MessageCircle, X, Send, ThumbsUp, MessageSquare,
  CheckCircle, Clock, FileEdit, PlayCircle, PauseCircle, PackageCheck, Tag,
  Wifi, WifiOff, Download, ExternalLink, Calendar, Instagram, Facebook, Linkedin,
  Chrome, Loader2, DollarSign, MousePointerClick, UserCheck, Globe, Scale, Lock,
  Languages, ListTree, Palette, Rocket, Sparkles, Info, Menu
} from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import DemoTour from '../components/DemoTour';

// ────────────────────────────────────────────────────────────────
// Sidebar - Client-facing only (Add-ons divider + Social/Ads/Video)
// ────────────────────────────────────────────────────────────────
const coreNav = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'keywords', label: 'Keywords & Queries', icon: Key },
  { id: 'link-building', label: 'Link Building', icon: Link2 },
  { id: 'position-tracking', label: 'Position Tracking', icon: TrendingUp },
  { id: 'traffic-analytics', label: 'Traffic Analytics', icon: BarChart3 },
  { id: 'content', label: 'Content & Assets', icon: FileText },
  { id: 'reporting', label: 'Reporting', icon: FileBarChart },
];
const addOnNav = [
  { id: 'social', label: 'Social Media', icon: Share2 },
  { id: 'paid-ads', label: 'Paid Ads', icon: Megaphone },
  { id: 'ai-video', label: 'AI Video', icon: Video },
  { id: 'website-creation', label: 'Website Creation', icon: Globe },
];
const specialNav = [
  { id: 'legal-translations', label: 'Legal Translations', icon: Scale },
];

function DemoSidebar({ activeTab, onTabChange }) {
  const renderItem = (item, special = false) => {
    const Icon = item.icon;
    const isActive = activeTab === item.id;
    if (special) {
      return (
        <button key={item.id} onClick={() => onTabChange(item.id)}
          className={`w-full relative flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 border ${
            isActive ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-600/40 border-violet-400/60'
              : 'text-violet-100 bg-gradient-to-r from-violet-900/40 to-indigo-900/30 border-violet-500/25 hover:border-violet-400/50 hover:from-violet-800/60 hover:to-indigo-800/50 hover:text-white shadow-md shadow-violet-900/20'
          }`}
          data-testid={`demo-nav-${item.id}`}>
          <Icon className="w-[18px] h-[18px]" />
          {item.label}
          <span className="ml-auto text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-violet-500/30 text-violet-100 border border-violet-400/30">Legal</span>
        </button>
      );
    }
    return (
      <button key={item.id} onClick={() => onTabChange(item.id)}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
          isActive ? 'bg-gradient-to-r from-violet-500/20 to-indigo-500/10 text-white border border-violet-500/20 shadow-lg shadow-violet-500/5'
            : 'text-slate-400 hover:text-white hover:bg-white/[0.05]'
        }`}
        data-testid={`demo-nav-${item.id}`}>
        <Icon className={`w-[18px] h-[18px] ${isActive ? 'text-violet-400' : ''}`} />
        {item.label}
      </button>
    );
  };

  return (
    <aside className="hidden md:flex flex-col bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 border-r border-white/[0.06] h-screen sticky top-0" data-testid="demo-sidebar">
      <div className="p-6 border-b border-white/[0.06]">
        <div className="flex items-center gap-3 mb-1">
          <BrandLogo size={40} />
          <div>
            <div className="font-bold text-white text-lg tracking-tight">Law Agency 360</div>
            <div className="text-xs text-slate-500">Demo Legal Firm</div>
          </div>
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wide">
          <Sparkles className="w-2.5 h-2.5" /> Authority Plan
        </div>
      </div>
      <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
        {coreNav.map((it) => renderItem(it))}
        {/* Add-ons divider */}
        <div className="pt-4 mt-2" data-testid="demo-addons-section">
          <div className="flex items-center gap-2 px-4 mb-2">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] font-semibold tracking-[0.15em] text-slate-500 uppercase">Add-ons</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          {addOnNav.map((it) => renderItem(it))}
          {/* Specialised divider */}
          <div className="flex items-center gap-2 px-4 mt-5 mb-3">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-violet-500/40 to-transparent" />
            <span className="text-[10px] font-semibold tracking-[0.15em] text-violet-300/80 uppercase">Specialised</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-violet-500/40 to-transparent" />
          </div>
          {specialNav.map((it) => renderItem(it, true))}
        </div>
      </nav>
    </aside>
  );
}

// ────────────────────────────────────────────────────────────────
// Shared atoms
// ────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    approved: { cls: 'bg-emerald-100 text-emerald-700', label: 'Approved', icon: CheckCircle },
    pending_approval: { cls: 'bg-amber-100 text-amber-700', label: 'Pending Approval', icon: Clock },
    draft: { cls: 'bg-slate-100 text-slate-600', label: 'Draft', icon: FileEdit },
    live: { cls: 'bg-green-100 text-green-700', label: 'Live', icon: PlayCircle },
    paused: { cls: 'bg-orange-100 text-orange-700', label: 'Paused', icon: PauseCircle },
    delivered: { cls: 'bg-blue-100 text-blue-700', label: 'Delivered', icon: PackageCheck },
    in_production: { cls: 'bg-violet-100 text-violet-700', label: 'In Production', icon: Loader2 },
    scheduled: { cls: 'bg-blue-100 text-blue-700', label: 'Scheduled', icon: Calendar },
    posted: { cls: 'bg-emerald-100 text-emerald-700', label: 'Posted', icon: CheckCircle },
    connected: { cls: 'bg-emerald-100 text-emerald-700', label: 'Connected', icon: Wifi },
    pending_access: { cls: 'bg-amber-100 text-amber-700', label: 'Pending Access', icon: Clock },
    not_connected: { cls: 'bg-slate-100 text-slate-600', label: 'Not Connected', icon: WifiOff },
  };
  const cfg = map[status] || map.draft;
  const Ic = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <Ic className={`w-3 h-3 ${status === 'in_production' ? 'animate-spin' : ''}`} />{cfg.label}
    </span>
  );
};

const ApprovalActions = ({ onApprove, onSuggest, testidKey }) => (
  <div className="flex items-center gap-2">
    <button onClick={onApprove} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors" data-testid={`demo-${testidKey}-approve`}>
      <ThumbsUp className="w-3.5 h-3.5" />Approve
    </button>
    <button onClick={onSuggest} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors" data-testid={`demo-${testidKey}-suggest`}>
      <MessageSquare className="w-3.5 h-3.5" />Suggest Changes
    </button>
  </div>
);

const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2200); };
  const node = toast ? (
    <div className="fixed top-6 right-6 z-50 px-4 py-3 bg-slate-900 text-white text-sm rounded-xl shadow-2xl border border-white/10 flex items-center gap-2 animate-fadeIn">
      <CheckCircle className="w-4 h-4 text-emerald-400" />{toast}
    </div>
  ) : null;
  return { show, node };
};

// ────────────────────────────────────────────────────────────────
// OVERVIEW
// ────────────────────────────────────────────────────────────────
function DemoOverview() {
  const topPrompts = [
    'best employment solicitor in london',
    'personal injury lawyer near me',
    'corporate law firm uk',
    'family law solicitor manchester',
  ];
  const competitive = [
    { name: 'Demo Legal Firm', citations: 2165, you: true },
    { name: 'Competitor A', citations: 1535 },
    { name: 'Competitor B', citations: 1107 },
  ];
  const activity = [
    { icon: FileText, title: '3 blogs submitted for approval', time: '2h ago' },
    { icon: Link2, title: '24 backlinks added', time: 'Yesterday' },
    { icon: Bot, title: 'AI visibility scan updated', time: '2 days ago' },
    { icon: Megaphone, title: 'Paid Ads performance updated', time: '3 days ago' },
    { icon: Linkedin, title: 'LinkedIn post approved', time: '4 days ago' },
    { icon: Globe, title: 'Website sitemap approved', time: '5 days ago' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Overview</h1>
        <p className="text-slate-500 mt-1">Real-time visibility into everything your agency is doing for your firm.</p>
      </div>

      {/* Total Citations hero */}
      <div className="bg-gradient-to-br from-violet-600 to-indigo-700 text-white rounded-3xl p-8 shadow-xl shadow-violet-500/30">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <span className="text-sm font-medium opacity-80">AI Visibility · Last updated 2h ago</span>
        </div>
        <div className="text-5xl font-bold mt-3" style={{ fontFamily: 'Outfit' }}>2,165</div>
        <div className="text-base mt-1 opacity-90">Total AI Citations across ChatGPT · Perplexity · Gemini</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top Prompts */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-1">Top Prompts You Appear In</h3>
          <p className="text-xs text-slate-500 mb-4">Where AI assistants reference your firm</p>
          <ul className="space-y-3">
            {topPrompts.map((p, i) => (
              <li key={i} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center text-xs font-bold">{i + 1}</div>
                <span className="text-sm text-slate-700">"{p}"</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Competitive AI Visibility */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-1">Competitive AI Visibility</h3>
          <p className="text-xs text-slate-500 mb-4">Citations across the major AI assistants</p>
          <div className="space-y-3">
            {competitive.map((c, i) => {
              const max = competitive[0].citations;
              const pct = (c.citations / max) * 100;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className={`font-medium ${c.you ? 'text-violet-700' : 'text-slate-600'}`}>{c.name}{c.you && <span className="ml-2 text-[10px] uppercase tracking-wide text-violet-500">You</span>}</span>
                    <span className="font-bold text-slate-700">{c.citations}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${c.you ? 'bg-gradient-to-r from-violet-500 to-indigo-500' : 'bg-slate-300'}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Organic Performance */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <h3 className="font-semibold text-slate-900 mb-4">Organic Performance · Last 30 days</h3>
        <div className="grid grid-cols-3 gap-5">
          {[
            { label: 'Sessions', value: '2,840', delta: '+12%' },
            { label: 'Total Users', value: '1,920', delta: '+8%' },
            { label: 'Page Views', value: '5,430', delta: '+15%' },
          ].map((m) => (
            <div key={m.label} className="bg-slate-50 rounded-xl p-4">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{m.label}</div>
              <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit' }}>{m.value}</div>
              <div className="text-xs text-emerald-600 font-medium mt-1 flex items-center gap-0.5">
                <ArrowUpRight className="w-3 h-3" />{m.delta}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <h3 className="font-semibold text-slate-900 mb-4">Recent Activity</h3>
        <ul className="space-y-3">
          {activity.map((a, i) => {
            const Ic = a.icon;
            return (
              <li key={i} className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                <div className="w-9 h-9 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center"><Ic className="w-4 h-4" /></div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-800">{a.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{a.time}</div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// KEYWORDS
// ────────────────────────────────────────────────────────────────
function DemoKeywords() {
  const { show, node } = useToast();
  const [items, setItems] = useState([
    { keyword: 'personal injury solicitor london', position: 5, status: 'approved' },
    { keyword: 'employment lawyer uk', position: 8, status: 'approved' },
    { keyword: 'conveyancing solicitor london', position: 6, status: 'pending_approval' },
    { keyword: 'family solicitor near me', position: 3, status: 'approved' },
  ]);
  const update = (i, status, msg) => {
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, status } : it));
    show(msg);
  };

  return (
    <div className="space-y-6">
      {node}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Keywords & Queries</h1>
        <p className="text-slate-500 mt-1">Every keyword we're targeting on your behalf.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Keyword</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Position</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((k, i) => (
              <tr key={i} className="hover:bg-violet-50/40 transition-colors">
                <td className="px-5 py-4 text-sm font-medium text-slate-900">{k.keyword}</td>
                <td className="px-5 py-4">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-50 text-violet-700 font-bold text-sm">#{k.position}</span>
                </td>
                <td className="px-5 py-4"><StatusBadge status={k.status} /></td>
                <td className="px-5 py-4 text-right">
                  {k.status === 'pending_approval' && (
                    <ApprovalActions
                      testidKey={`kw-${i}`}
                      onApprove={() => update(i, 'approved', 'Keyword approved')}
                      onSuggest={() => update(i, 'pending_approval', 'Feedback noted (demo)')}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// LINK BUILDING
// ────────────────────────────────────────────────────────────────
function DemoLinkBuilding() {
  const batches = [
    { name: 'March Authority Links', count: 12, date: '14 Mar 2026' },
    { name: 'February Local Citations', count: 9, date: '20 Feb 2026' },
    { name: 'January Legal Directories', count: 14, date: '18 Jan 2026' },
  ];
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Link Building</h1>
        <p className="text-slate-500 mt-1">Every backlink we've built, logged in real time.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="text-xs font-medium text-slate-500 uppercase">Total Backlinks Built</div>
          <div className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit' }}>56</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="text-xs font-medium text-slate-500 uppercase">This Month</div>
          <div className="text-3xl font-bold text-emerald-600 mt-1" style={{ fontFamily: 'Outfit' }}>+12</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="text-xs font-medium text-slate-500 uppercase">Avg DA</div>
          <div className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit' }}>47</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Backlink Batches</h3>
          <button className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-violet-50 text-violet-700 hover:bg-violet-100 rounded-lg font-medium transition-colors">
            <ExternalLink className="w-3.5 h-3.5" />View Google Sheet
          </button>
        </div>
        <ul className="divide-y divide-slate-100">
          {batches.map((b, i) => (
            <li key={i} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
              <div>
                <div className="font-medium text-slate-900">{b.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{b.date}</div>
              </div>
              <span className="px-3 py-1 bg-violet-50 text-violet-700 rounded-full text-xs font-bold">+{b.count} links</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// POSITION TRACKING
// ────────────────────────────────────────────────────────────────
function DemoPositionTracking() {
  const [tab, setTab] = useState('ai');
  const aiPrompts = [
    { prompt: 'best personal injury lawyer in london', platform: 'ChatGPT', position: 2, vis: 78.5 },
    { prompt: 'employment lawyer uk', platform: 'Perplexity', position: 3, vis: 65.0 },
    { prompt: 'conveyancing solicitor london', platform: 'Gemini', position: 1, vis: 92.3 },
    { prompt: 'family law firm near me', platform: 'ChatGPT', position: 4, vis: 45.0 },
  ];
  const seoRanks = [
    { keyword: 'personal injury solicitor london', position: 5, prev: 8, change: '+3' },
    { keyword: 'employment lawyer uk', position: 8, prev: 12, change: '+4' },
    { keyword: 'conveyancing solicitor london', position: 6, prev: 5, change: '−1' },
    { keyword: 'family solicitor near me', position: 3, prev: 4, change: '+1' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Position Tracking</h1>
        <p className="text-slate-500 mt-1">Where you rank - across Google and AI assistants.</p>
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        <button onClick={() => setTab('ai')} className={`px-5 py-2 rounded-lg text-sm font-medium ${tab === 'ai' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>AI Visibility</button>
        <button onClick={() => setTab('seo')} className={`px-5 py-2 rounded-lg text-sm font-medium ${tab === 'seo' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Search Engine Rankings</button>
      </div>

      {tab === 'ai' ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="text-xs font-medium text-slate-500 uppercase">Total Citations</div>
              <div className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit' }}>51</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="text-xs font-medium text-slate-500 uppercase">Unique Domains</div>
              <div className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit' }}>32</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="text-xs font-medium text-slate-500 uppercase">Avg Visibility</div>
              <div className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit' }}>70%</div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Prompt</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Platform</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Position</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Visibility</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {aiPrompts.map((p, i) => (
                  <tr key={i} className="hover:bg-violet-50/30 transition-colors">
                    <td className="px-5 py-3 text-sm text-slate-800">"{p.prompt}"</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{p.platform}</td>
                    <td className="px-5 py-3"><span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-violet-50 text-violet-700 font-bold text-xs">#{p.position}</span></td>
                    <td className="px-5 py-3 text-sm font-semibold text-slate-700">{p.vis.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <select className="px-4 py-2 border border-slate-200 rounded-xl text-sm bg-white" disabled>
              <option>Last 30 days</option>
            </select>
            <span className="text-xs text-slate-400">(demo · custom range disabled)</span>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Keyword</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Previous</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Current</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Change</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {seoRanks.map((r, i) => (
                  <tr key={i} className="hover:bg-violet-50/30 transition-colors">
                    <td className="px-5 py-3 text-sm font-medium text-slate-800">{r.keyword}</td>
                    <td className="px-5 py-3 text-sm text-slate-500">#{r.prev}</td>
                    <td className="px-5 py-3"><span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-violet-50 text-violet-700 font-bold text-xs">#{r.position}</span></td>
                    <td className={`px-5 py-3 text-sm font-bold ${r.change.startsWith('+') ? 'text-emerald-600' : 'text-red-500'}`}>{r.change}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// TRAFFIC ANALYTICS
// ────────────────────────────────────────────────────────────────
function DemoTrafficAnalytics() {
  const trend = [40, 55, 48, 62, 58, 70, 65, 80, 72, 85, 78, 90, 88, 95];
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Traffic Analytics</h1>
          <p className="text-slate-500 mt-1">Connected directly to Google Analytics + Search Console.</p>
        </div>
        <select className="px-4 py-2 border border-slate-200 rounded-xl text-sm bg-white" disabled>
          <option>Last 30 days</option>
        </select>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Sessions', value: '1,240', icon: Activity, grad: 'from-violet-500 to-indigo-600' },
          { label: 'Users', value: '860', icon: Eye, grad: 'from-blue-500 to-cyan-500' },
          { label: 'Page Views', value: '2,100', icon: Target, grad: 'from-emerald-500 to-teal-500' },
        ].map((m, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-slate-500 uppercase">{m.label}</span>
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${m.grad} flex items-center justify-center`}>
                <m.icon className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{m.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <h3 className="font-semibold text-slate-900 mb-4">Traffic Trend · Last 14 days</h3>
        <div className="h-48 flex items-end gap-1.5">
          {trend.map((h, i) => (
            <div key={i} className="flex-1 bg-gradient-to-t from-violet-600 to-violet-300 rounded-t-md hover:from-violet-700 transition-all" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// CONTENT & ASSETS
// ────────────────────────────────────────────────────────────────
function DemoContent() {
  const { show, node } = useToast();
  const [items, setItems] = useState([
    { type: 'Blog', title: 'Guide to Personal Injury Claims in 2026', status: 'pending_approval' },
    { type: 'Page', title: 'Employment Law landing page optimised', status: 'approved' },
    { type: 'Meta', title: 'Updated meta titles for 12 service pages', status: 'approved' },
    { type: 'FAQ', title: 'Added FAQ section to Family Law page', status: 'approved' },
    { type: 'Technical', title: 'Schema markup deployed across site', status: 'approved' },
    { type: 'Blog', title: 'How to Choose a Conveyancing Solicitor', status: 'pending_approval' },
  ]);
  const update = (i, status, msg) => {
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, status } : it));
    show(msg);
  };

  return (
    <div className="space-y-6">
      {node}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Content & Assets</h1>
        <p className="text-slate-500 mt-1">Every piece of content goes through your approval before it goes live.</p>
      </div>

      <div className="grid gap-3">
        {items.map((it, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <span className="px-3 py-1 bg-violet-50 text-violet-700 rounded-lg text-xs font-bold">{it.type}</span>
              <div>
                <div className="font-medium text-slate-900">{it.title}</div>
                <StatusBadge status={it.status} />
              </div>
            </div>
            {it.status === 'pending_approval' && (
              <ApprovalActions
                testidKey={`content-${i}`}
                onApprove={() => update(i, 'approved', 'Content approved')}
                onSuggest={() => update(i, 'pending_approval', 'Feedback noted (demo)')}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// REPORTING
// ────────────────────────────────────────────────────────────────
function DemoReporting() {
  const { show, node } = useToast();
  const reports = [
    { title: 'Monthly Digital Marketing Performance Report', month: 'March 2026', period: '1 Mar - 31 Mar 2026', generated: '1 Apr 2026' },
    { title: 'Monthly Digital Marketing Performance Report', month: 'February 2026', period: '1 Feb - 28 Feb 2026', generated: '1 Mar 2026' },
    { title: 'Monthly Digital Marketing Performance Report', month: 'January 2026', period: '1 Jan - 31 Jan 2026', generated: '1 Feb 2026' },
  ];

  return (
    <div className="space-y-6">
      {node}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Reporting</h1>
        <p className="text-slate-500 mt-1">Branded monthly performance reports - ready to download.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {reports.map((r, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                <FileBarChart className="w-6 h-6 text-white" />
              </div>
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">Generated</span>
            </div>
            <h3 className="font-bold text-slate-900 mb-1">{r.title}</h3>
            <div className="text-xs text-slate-500">{r.month}</div>
            <div className="mt-4 space-y-1.5 text-xs text-slate-600">
              <div>Period: <span className="font-medium text-slate-700">{r.period}</span></div>
              <div>Generated: <span className="font-medium text-slate-700">{r.generated}</span></div>
            </div>
            <button onClick={() => show('PDF download (demo)')}
              className="w-full mt-5 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:shadow-violet-500/30 transition-all">
              <Download className="w-4 h-4" />Download PDF
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// SOCIAL MEDIA
// ────────────────────────────────────────────────────────────────
function DemoSocial() {
  const { show, node } = useToast();
  const accounts = [
    { platform: 'LinkedIn', icon: Linkedin, color: 'from-sky-600 to-blue-700', status: 'connected' },
    { platform: 'Facebook', icon: Facebook, color: 'from-blue-500 to-blue-600', status: 'pending_access' },
    { platform: 'Instagram', icon: Instagram, color: 'from-pink-500 to-purple-500', status: 'connected' },
  ];
  const [posts, setPosts] = useState([
    { id: 1, platform: 'LinkedIn', icon: Linkedin, color: 'from-sky-600 to-blue-700',
      title: 'Why every law firm needs a content strategy in 2026',
      caption: '5 reasons solicitors should invest in thought-leadership content this year.',
      status: 'pending_approval' },
    { id: 2, platform: 'Instagram', icon: Instagram, color: 'from-pink-500 to-purple-500',
      title: 'Know your rights - Employment Law tips',
      caption: 'A quick guide to the questions every employee should ask.',
      status: 'scheduled' },
    { id: 3, platform: 'Facebook', icon: Facebook, color: 'from-blue-500 to-blue-600',
      title: 'Meet our Family Law team',
      caption: 'Our solicitors are here to help you through life\'s difficult chapters.',
      status: 'posted' },
  ]);
  const update = (id, status, msg) => { setPosts(prev => prev.map(p => p.id === id ? { ...p, status } : p)); show(msg); };

  return (
    <div className="space-y-6">
      {node}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Social Media</h1>
        <p className="text-slate-500 mt-1">Posts created by your agency, reviewed by you.</p>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Account Access</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {accounts.map(a => {
            const Ic = a.icon;
            return (
              <div key={a.platform} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${a.color} flex items-center justify-center`}>
                  <Ic className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{a.platform}</div>
                  <StatusBadge status={a.status} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Posts</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {posts.map(p => {
            const Ic = p.icon;
            return (
              <div key={p.id} className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${p.color}`} />
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${p.color} flex items-center justify-center`}>
                      <Ic className="w-4 h-4 text-white" />
                    </div>
                    <StatusBadge status={p.status} />
                  </div>
                  <h4 className="font-semibold text-slate-900">{p.title}</h4>
                  <p className="text-sm text-slate-600 mt-1.5 line-clamp-2">{p.caption}</p>
                  {p.status === 'pending_approval' && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <ApprovalActions
                        testidKey={`social-${p.id}`}
                        onApprove={() => update(p.id, 'approved', 'Social post approved')}
                        onSuggest={() => update(p.id, 'pending_approval', 'Feedback sent (demo)')}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// PAID ADS
// ────────────────────────────────────────────────────────────────
function DemoPaidAds() {
  const { show, node } = useToast();
  const [campaigns, setCampaigns] = useState([
    { id: 1, name: 'Personal Injury Leads', platform: 'Google Ads', icon: Chrome, color: 'from-yellow-400 via-red-500 to-blue-500', budget: 2000, status: 'live' },
    { id: 2, name: 'Brand Awareness Campaign', platform: 'Meta Ads', icon: Facebook, color: 'from-blue-500 to-purple-600', budget: 1200, status: 'pending_approval' },
  ]);
  const update = (id, status, msg) => { setCampaigns(prev => prev.map(c => c.id === id ? { ...c, status } : c)); show(msg); };

  const perfRow = {
    period: '25 Mar - 24 Apr 2026',
    spend: 1500, impressions: 25000, clicks: 1200, leads: 45, ctr: 4.8, cpl: 33,
  };

  return (
    <div className="space-y-6">
      {node}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Paid Ads</h1>
        <p className="text-slate-500 mt-1">You approve every ad before it goes live.</p>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Campaigns</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {campaigns.map(c => {
            const Ic = c.icon;
            return (
              <div key={c.id} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center`}>
                    <Ic className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-slate-900">{c.name}</h4>
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{c.platform} · £{c.budget.toLocaleString()}/month</div>
                  </div>
                </div>
                {c.status === 'pending_approval' && (
                  <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end">
                    <ApprovalActions
                      testidKey={`ads-${c.id}`}
                      onApprove={() => update(c.id, 'approved', 'Campaign approved')}
                      onSuggest={() => update(c.id, 'pending_approval', 'Feedback sent (demo)')}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Performance · Last 30 days</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Spend', value: `£${perfRow.spend.toLocaleString()}`, icon: DollarSign, grad: 'from-violet-500 to-indigo-600' },
            { label: 'Impressions', value: perfRow.impressions.toLocaleString(), icon: Eye, grad: 'from-blue-500 to-cyan-500' },
            { label: 'Clicks', value: perfRow.clicks.toLocaleString(), icon: MousePointerClick, grad: 'from-fuchsia-500 to-pink-500' },
            { label: 'Leads', value: perfRow.leads, icon: TrendingUp, grad: 'from-emerald-500 to-teal-500' },
          ].map((m, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-500 uppercase">{m.label}</span>
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${m.grad} flex items-center justify-center`}>
                  <m.icon className="w-4 h-4 text-white" />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{m.value}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="text-xs font-medium text-slate-500 uppercase">CTR</div>
            <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit' }}>{perfRow.ctr}%</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="text-xs font-medium text-slate-500 uppercase">Cost per Lead</div>
            <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit' }}>£{perfRow.cpl}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// AI VIDEO
// ────────────────────────────────────────────────────────────────
function DemoAIVideo() {
  const { show, node } = useToast();
  const [videos, setVideos] = useState([
    { id: 1, title: 'Personal Injury Ad Video', platform: 'Instagram', duration: '30 sec', status: 'pending_approval' },
    { id: 2, title: 'Employment Law Explainer', platform: 'LinkedIn', duration: '60 sec', status: 'in_production' },
    { id: 3, title: 'Brand Awareness Video', platform: 'Facebook', duration: '30 sec', status: 'delivered' },
  ]);
  const update = (id, status, msg) => { setVideos(prev => prev.map(v => v.id === id ? { ...v, status } : v)); show(msg); };

  return (
    <div className="space-y-6">
      {node}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>AI Video</h1>
        <p className="text-slate-500 mt-1">Request, review and download AI-assisted videos for your firm.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {videos.map(v => (
          <div key={v.id} className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="h-40 bg-gradient-to-br from-slate-900 via-violet-900/60 to-slate-800 relative flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-xl">
                <PlayCircle className="w-7 h-7 text-violet-600 fill-violet-600" />
              </div>
              <span className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/70 text-white text-xs rounded-md font-medium">{v.duration}</span>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">{v.platform}</span>
                <StatusBadge status={v.status} />
              </div>
              <h4 className="font-semibold text-slate-900">{v.title}</h4>
              {v.status === 'pending_approval' && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <ApprovalActions
                    testidKey={`video-${v.id}`}
                    onApprove={() => update(v.id, 'approved', 'Video approved')}
                    onSuggest={() => update(v.id, 'pending_approval', 'Feedback sent (demo)')}
                  />
                </div>
              )}
              {v.status === 'delivered' && (
                <button onClick={() => show('Download (demo)')} className="mt-3 w-full px-3 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5">
                  <Download className="w-3.5 h-3.5" />Download
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// CHAT BUBBLE (mock)
// ────────────────────────────────────────────────────────────────
function DemoChatBubble({ openSignal = 0 }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(1);
  const [text, setText] = useState('');
  const [messages, setMessages] = useState([
    { sender: 'client', body: 'When will the next blog be ready?', time: '30m ago' },
    { sender: 'team', body: 'It will be ready for your approval this Friday.', time: '20m ago' },
    { sender: 'client', body: 'Perfect, thank you.', time: '10m ago' },
  ]);

  // Open the panel whenever a parent signals it (postMessage from landing page)
  useEffect(() => {
    if (openSignal > 0) {
      setOpen(true);
      setUnread(0);
    }
  }, [openSignal]);

  const openPanel = () => { setOpen(true); setUnread(0); };
  const sendMock = () => {
    if (!text.trim()) return;
    setMessages([...messages, { sender: 'client', body: text.trim(), time: 'just now' }]);
    setText('');
  };

  return (
    <>
      <button onClick={openPanel} className="fixed bottom-6 right-6 z-40 group flex items-center gap-2.5 pl-3 pr-5 py-3 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-2xl shadow-xl shadow-violet-600/40 hover:scale-[1.03] transition-all" data-testid="demo-chat-bubble">
        <div className="relative w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
          <MessageCircle className="w-5 h-5 text-white" />
          {unread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] bg-red-500 border-2 border-violet-600 text-white text-[10px] rounded-full flex items-center justify-center px-1 font-bold shadow-lg">
              {unread}
            </span>
          )}
        </div>
        <span className="hidden sm:inline text-sm font-semibold text-white whitespace-nowrap">Chat with Account Manager</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="w-full sm:w-[460px] bg-slate-950 border-l border-white/10 flex flex-col shadow-2xl"
            style={{ animation: 'slideIn 280ms cubic-bezier(0.22, 1, 0.36, 1)' }}>
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between bg-slate-900">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-violet-400" />
                <span className="text-white font-semibold">Messages</span>
                <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded-full font-medium">Demo</span>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/5 rounded-lg text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-4 py-3 border-b border-white/5 flex items-center gap-3 bg-slate-900/40">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">Sarah · Account Manager</div>
                <div className="text-[11px] text-slate-500">Your dedicated point of contact</div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.sender === 'client' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] ${m.sender === 'client' ? 'bg-gradient-to-br from-violet-600 to-indigo-600 text-white' : 'bg-slate-800 text-slate-100'} rounded-2xl px-3.5 py-2.5`}>
                    <div className="text-sm leading-relaxed">{m.body}</div>
                    <div className={`text-[10px] mt-1 ${m.sender === 'client' ? 'text-white/60' : 'text-slate-500'}`}>{m.time}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-white/5 p-3 bg-slate-900/80">
              <div className="flex items-center gap-2 mb-2">
                <Tag className="w-3.5 h-3.5 text-slate-500" />
                <select disabled className="text-xs bg-slate-800 text-slate-200 border border-white/5 rounded-lg px-2 py-1">
                  <option>General</option>
                </select>
              </div>
              <div className="flex items-end gap-2">
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
                  placeholder="Write a message to your account manager…"
                  className="flex-1 bg-slate-800 text-slate-100 placeholder-slate-500 border border-white/5 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 resize-none" />
                <button onClick={sendMock} className="p-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 200ms ease-out; }
      `}</style>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// WEBSITE CREATION (mock)
// ────────────────────────────────────────────────────────────────
function DemoWebsiteCreation() {
  const [tab, setTab] = useState('brief');
  const tabs = [
    { id: 'brief', label: 'Project Brief', icon: FileText },
    { id: 'assets', label: 'Assets', icon: FileEdit },
    { id: 'sitemap', label: 'Sitemap', icon: ListTree },
    { id: 'design', label: 'Design', icon: Palette },
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'progress', label: 'Build Progress', icon: Activity },
    { id: 'launch', label: 'Launch', icon: Rocket }
  ];
  const stages = [
    { id: 'brief_received', label: 'Brief Received' },
    { id: 'sitemap_approved', label: 'Sitemap Approved' },
    { id: 'design_in_progress', label: 'Design In Progress' },
    { id: 'design_approved', label: 'Design Approved', current: true },
    { id: 'content_approved', label: 'Content Approved' },
    { id: 'development_in_progress', label: 'Development In Progress' },
    { id: 'qa_testing', label: 'QA Testing' },
    { id: 'ready_for_launch', label: 'Ready for Launch' },
    { id: 'launched', label: 'Launched' }
  ];
  const currentIdx = stages.findIndex(s => s.current);
  const pct = Math.round(((currentIdx + 1) / stages.length) * 100);
  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-md shadow-violet-500/30">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Website Creation</h1>
          <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold uppercase tracking-wide">Add-on</span>
        </div>
        <p className="text-slate-500">Plan, design and ship a website your firm can be proud of.</p>
      </div>
      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-2 whitespace-nowrap ${tab === t.id ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {tab === 'brief' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <DemoField label="Website Type">
              <DemoRadios
                options={['New Website', 'Redesign', 'Landing Page']}
                selected="Redesign"
              />
            </DemoField>
            <DemoField label="Current Website URL">
              <DemoInput value="www.demolegalfirm.co.uk" />
            </DemoField>
            <DemoField label="Practice Areas / Services">
              <DemoInput value="Employment Law, Family Law, Personal Injury" />
            </DemoField>
            <DemoField label="Target Locations">
              <DemoInput value="London, Manchester, Birmingham" />
            </DemoField>
            <DemoField label="Competitor Websites">
              <DemoInput value="slatergordon.co.uk, irwinmitchell.com" />
            </DemoField>
            <DemoField label="Design Preferences">
              <DemoInput value="Modern, professional, dark accent" />
            </DemoField>
            <DemoField label="Required Pages" full>
              <div className="flex flex-wrap gap-1.5">
                {['Home', 'About', 'Practice Areas', 'Team', 'Insights', 'Contact'].map(p => (
                  <span key={p} className="px-2 py-0.5 rounded-md bg-violet-50 border border-violet-100 text-violet-700 text-xs font-medium">{p}</span>
                ))}
              </div>
            </DemoField>
            <DemoField label="Notes" full>
              <DemoTextarea value="Please retain blog content from current site. Need GDPR cookie banner. Booking integration with Calendly preferred." />
            </DemoField>
          </div>

          <div className="border-2 border-dashed border-slate-200 rounded-xl p-5 flex items-center gap-3 bg-slate-50/60">
            <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center"><FileEdit className="w-5 h-5" /></div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">Drop logos, brand guidelines or reference designs</div>
              <div className="text-xs text-slate-500">PDF, PNG, SVG up to 25MB · Sample brief - no real upload</div>
            </div>
            <button disabled className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-500 text-xs font-medium cursor-not-allowed">Upload</button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Info className="w-3.5 h-3.5 text-violet-500" />
              <span>Sample preview · brief is auto-saved as you type in the live dashboard</span>
            </div>
            <button
              type="button"
              disabled
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold shadow-md shadow-violet-500/20 opacity-90 cursor-not-allowed"
              data-testid="demo-submit-brief"
            >
              Submit Brief
            </button>
          </div>

          {/* Sample project progress preview */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs text-slate-500">Project progress preview</div>
                <div className="text-sm font-bold text-slate-900">Currently: Design Approved</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-violet-600">{pct}%</div>
                <div className="text-[10px] text-slate-500">complete</div>
              </div>
            </div>
            <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-violet-500 to-indigo-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      )}

      {tab === 'assets' && (
        <div className="grid sm:grid-cols-3 gap-3">
          {[{ n: 'logo.svg', by: 'admin', d: '12 Mar' }, { n: 'team_photo_1.jpg', by: 'admin', d: '12 Mar' }, { n: 'brand_guidelines.pdf', by: 'admin', d: '14 Mar' }].map((a) => (
            <div key={a.n} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center"><FileText className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate">{a.n}</div>
                <div className="text-[10px] text-slate-500">By {a.by} · {a.d}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'sitemap' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-slate-900">Proposed Structure</h4>
            <span className="px-2.5 py-1 rounded-full text-xs font-medium border bg-emerald-100 text-emerald-700 border-emerald-200">Approved</span>
          </div>
          {[
            { t: 'Home' }, { t: 'About' }, { t: 'Services', kids: ['Employment Law', 'Personal Injury', 'Corporate Law'] }, { t: 'Contact' }
          ].map((p, i) => (
            <div key={i} className="py-1">
              <div className="text-sm font-medium text-slate-800">▸ {p.t}</div>
              {p.kids && p.kids.map((k, j) => <div key={j} className="text-sm text-slate-600 ml-6">▸ {k}</div>)}
            </div>
          ))}
        </div>
      )}

      {tab === 'design' && (
        <div className="grid md:grid-cols-2 gap-4">
          {[{ label: 'Homepage mockup', status: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' }, { label: 'Service page mockup', status: 'Pending Approval', cls: 'bg-amber-100 text-amber-700 border-amber-200' }].map((d, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="aspect-video bg-gradient-to-br from-slate-100 via-violet-50 to-indigo-100 flex items-center justify-center"><Palette className="w-10 h-10 text-violet-400" /></div>
              <div className="p-4 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-900">{d.label}</div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${d.cls}`}>{d.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'content' && (
        <div className="space-y-3">
          {[{ p: 'Home page content', s: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' }, { p: 'Employment Law content', s: 'Pending Approval', cls: 'bg-amber-100 text-amber-700 border-amber-200' }].map((c, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between gap-3">
              <div><div className="text-sm font-bold text-slate-900">{c.p}</div><div className="text-xs text-slate-500 mt-1">Last updated 2 days ago</div></div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${c.cls}`}>{c.s}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'progress' && (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs text-slate-500">Current stage</div>
                <div className="text-lg font-bold text-slate-900">Design Approved</div>
              </div>
              <div className="text-right"><div className="text-3xl font-bold text-violet-600">{pct}%</div><div className="text-xs text-slate-500">complete</div></div>
            </div>
            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-violet-500 to-indigo-500" style={{ width: `${pct}%` }} /></div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h4 className="text-sm font-semibold text-slate-900 mb-4">Timeline</h4>
            <div className="space-y-3">
              {stages.map((s, i) => {
                const done = i < currentIdx; const active = i === currentIdx;
                return (
                  <div key={s.id} className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${done ? 'bg-emerald-500 text-white' : active ? 'bg-violet-600 text-white ring-4 ring-violet-200' : 'bg-slate-200 text-slate-500'}`}>{done ? <CheckCircle className="w-4 h-4" /> : i + 1}</div>
                    <div className={`text-sm ${active ? 'font-bold text-slate-900' : done ? 'text-slate-700' : 'text-slate-500'}`}>{s.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {tab === 'launch' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><div className="text-xs font-semibold text-slate-500 uppercase">Staging URL</div><div className="text-sm text-violet-600 mt-1">staging.demolegalfirm.co.uk</div></div>
            <div><div className="text-xs font-semibold text-slate-500 uppercase">Final URL</div><div className="text-sm text-slate-400 mt-1">Not yet live</div></div>
            <div><div className="text-xs font-semibold text-slate-500 uppercase">Launch Date</div><div className="text-sm text-slate-900 mt-1">15 Apr 2026 (planned)</div></div>
            <div><div className="text-xs font-semibold text-slate-500 uppercase">Status</div><div className="mt-1"><span className="px-2.5 py-1 rounded-full text-xs font-medium border bg-amber-100 text-amber-700 border-amber-200">Pending Approval</span></div></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// LEGAL TRANSLATIONS (mock)
// ────────────────────────────────────────────────────────────────
function DemoLegalTranslations() {
  const [tab, setTab] = useState('list');
  const requests = [
    { id: 'a1b2c3d4', src: 'English', tgt: 'French', type: 'ai_human', status: 'Ready for Download', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', words: 2450, rate: 0.1547, total: 378.99, name: 'Contract Translation' },
    { id: 'e5f6g7h8', src: 'German', tgt: 'English', type: 'ai_human', status: 'In Review', cls: 'bg-amber-100 text-amber-700 border-amber-200', words: 1800, rate: 0.182, total: 327.6, name: 'Legal Agreement Translation' },
    { id: 'i9j0k1l2', src: 'English', tgt: 'Spanish', type: 'ai', status: 'Submitted', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200', words: 950, rate: null, total: null, name: 'Court Document Translation' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-md shadow-violet-500/30">
            <Languages className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Legal Translations</h1>
          <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold uppercase tracking-wide">Add-on</span>
        </div>
        <p className="text-slate-500">Upload legal documents, get AI or human-reviewed translations, and download when ready.</p>
      </div>

      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 w-fit">
        {[{ id: 'new', l: 'New Request' }, { id: 'list', l: 'My Translation Requests' }, { id: 'downloads', l: 'Downloads' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${tab === t.id ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'new' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5" data-testid="demo-translation-form">
          {/* Upload area */}
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Upload Document</div>
            <div className="border-2 border-dashed border-violet-200 bg-violet-50/40 rounded-xl p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center"><FileText className="w-5 h-5" /></div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">contract_employment_2026.pdf</div>
                <div className="text-xs text-slate-500">Selected · 312KB · PDF · Sample upload - no real file is sent</div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-xs font-medium border bg-emerald-100 text-emerald-700 border-emerald-200">Ready</span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <DemoField label="Source Language">
              <DemoSelect value="English" options={['English', 'German', 'French', 'Spanish', 'Italian']} />
            </DemoField>
            <DemoField label="Target Language">
              <DemoSelect value="French" options={['French', 'German', 'Spanish', 'Italian', 'Portuguese', 'Arabic']} />
            </DemoField>
            <DemoField label="Translation Type">
              <DemoSelect value="AI + Human Review" options={['AI Translation', 'AI + Human Review']} />
            </DemoField>
            <DemoField label="Urgency">
              <DemoSelect value="Standard" options={['Standard', 'Urgent']} />
            </DemoField>
            <DemoField label="Notes / Instructions" full>
              <DemoTextarea value="Maintain capitalised legal terms verbatim. Preserve original heading structure. Apply UK English conventions where applicable." />
            </DemoField>
          </div>

          {/* Pricing summary - word count visible, rates blurred */}
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-violet-50/40 p-5">
            <div className="text-xs font-semibold text-slate-500 uppercase mb-3">Pricing Summary</div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-slate-500">Estimated Word Count</div>
                <div className="text-lg font-bold text-slate-900 mt-0.5" data-testid="demo-translation-word-count">2,450</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Per Word Rate</div>
                <div
                  className="text-lg font-bold text-slate-900 mt-0.5 select-none"
                  style={{ filter: 'blur(6px)', letterSpacing: '0.1em' }}
                  aria-hidden="true"
                  data-testid="demo-translation-rate-blurred"
                >
                  $0.1547
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Estimated Total</div>
                <div
                  className="text-lg font-bold text-slate-900 mt-0.5 select-none"
                  style={{ filter: 'blur(6px)', letterSpacing: '0.1em' }}
                  aria-hidden="true"
                  data-testid="demo-translation-total-blurred"
                >
                  $378.99
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 text-xs text-slate-500">
              <Lock className="w-3.5 h-3.5 text-violet-500" />
              <span>Live rate is calculated against your source/target language pair after sign-up.</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-slate-100">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Info className="w-3.5 h-3.5 text-violet-500" />
              <span>Sample preview · no document is uploaded and no request is created</span>
            </div>
            <button
              type="button"
              disabled
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold shadow-md shadow-violet-500/20 opacity-90 cursor-not-allowed inline-flex items-center gap-2"
              data-testid="demo-submit-translation"
            >
              <Languages className="w-4 h-4" /> Submit Translation Request
            </button>
          </div>
        </div>
      )}

      {tab === 'list' && (
        <div className="space-y-3">
          {requests.map(r => (
            <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs font-mono text-slate-500">#{r.id}</span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${r.cls}`}>{r.status}</span>
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 mt-2">{r.src} → {r.tgt}</h3>
                  <div className="text-xs text-slate-500 mt-0.5">{r.name}</div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                    <span className="inline-flex items-center gap-1">{r.type === 'ai' ? <Bot className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}{r.type === 'ai' ? 'AI Translation' : 'AI + Human Review'}</span>
                    <span>•</span><span>{r.words.toLocaleString()} words</span>
                    {r.rate && (
                      <>
                        <span>•</span>
                        <span className="select-none" style={{ filter: 'blur(4px)', letterSpacing: '0.08em' }} aria-hidden="true">${r.rate.toFixed(4)}/word</span>
                        <span>•</span>
                        <span className="font-semibold text-violet-700 select-none" style={{ filter: 'blur(4px)', letterSpacing: '0.08em' }} aria-hidden="true">${r.total.toFixed(2)}</span>
                      </>
                    )}
                  </div>
                </div>
                {r.status === 'Ready for Download' && (
                  <button className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium"><Download className="w-4 h-4" />Download Translated</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'downloads' && (
        <div className="space-y-3">
          {requests.filter(r => r.status === 'Ready for Download').map(r => (
            <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs text-slate-500">#{r.id}</div>
                <div className="text-sm font-semibold text-slate-900 mt-1">{r.src} → {r.tgt}</div>
                <div className="text-xs text-slate-500 mt-1">Completed {new Date().toLocaleDateString()}</div>
              </div>
              <button className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium"><Download className="w-4 h-4" />Translated File</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// SHARED FORM PRIMITIVES (demo-only - visual mock, no real input handling)
// ────────────────────────────────────────────────────────────────
function DemoField({ label, full, children }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function DemoInput({ value }) {
  return (
    <input
      readOnly
      tabIndex={-1}
      value={value}
      className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 cursor-default focus:outline-none focus:ring-2 focus:ring-violet-200"
    />
  );
}

function DemoTextarea({ value }) {
  return (
    <textarea
      readOnly
      tabIndex={-1}
      value={value}
      rows={3}
      className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 cursor-default focus:outline-none focus:ring-2 focus:ring-violet-200 resize-none"
    />
  );
}

function DemoSelect({ value, options = [] }) {
  return (
    <div className="relative">
      <select
        disabled
        value={value}
        className="w-full appearance-none px-3 py-2 pr-9 rounded-lg bg-white border border-slate-200 text-sm font-medium text-slate-900 cursor-default disabled:opacity-100"
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">▾</span>
    </div>
  );
}

function DemoRadios({ options = [], selected }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(o => {
        const isOn = o === selected;
        return (
          <span
            key={o}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
              isOn
                ? 'bg-violet-600 border-violet-600 text-white shadow-sm shadow-violet-500/30'
                : 'bg-white border-slate-200 text-slate-700'
            }`}
          >
            {o}
          </span>
        );
      })}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// TAB MAP + MAIN PAGE
// ────────────────────────────────────────────────────────────────
const TAB_MAP = {
  'overview': DemoOverview,
  'keywords': DemoKeywords,
  'link-building': DemoLinkBuilding,
  'position-tracking': DemoPositionTracking,
  'traffic-analytics': DemoTrafficAnalytics,
  'content': DemoContent,
  'reporting': DemoReporting,
  'social': DemoSocial,
  'paid-ads': DemoPaidAds,
  'ai-video': DemoAIVideo,
  'website-creation': DemoWebsiteCreation,
  'legal-translations': DemoLegalTranslations,
};

export default function DemoDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [chatOpenSignal, setChatOpenSignal] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const ActiveTab = TAB_MAP[activeTab];

  // Listen for postMessage from parent landing page to open the chat bubble
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'clariodash:open-chat') {
        setChatOpenSignal((n) => n + 1);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Lock body scroll when drawer open
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  // Flat list for the mobile horizontal tab strip
  const ALL_TABS = [...coreNav, ...addOnNav, ...specialNav];

  const handleTabChange = (id) => {
    setActiveTab(id);
    setDrawerOpen(false);
    // Scroll content area to top after switching on mobile
    if (typeof window !== 'undefined') {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-slate-50 max-w-full overflow-x-hidden" data-testid="demo-dashboard">
      {/* Desktop Sidebar - hidden below md */}
      <DemoSidebar activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Mobile/Tablet Top Bar - visible below md */}
      <header
        className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-slate-950/95 backdrop-blur border-b border-white/10"
        data-testid="demo-mobile-topbar"
      >
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white/10 border border-white/15 text-white active:scale-95 transition"
          aria-label="Open dashboard menu"
          data-testid="demo-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <BrandLogo size={28} />
          <div className="min-w-0">
            <div className="text-white text-sm font-semibold truncate">Law Agency 360</div>
            <div className="text-slate-400 text-[10px] truncate">Demo Legal Firm</div>
          </div>
        </div>
        <span className="hidden xs:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">
          Authority
        </span>
      </header>

      {/* Mobile/Tablet horizontal scroll tab strip */}
      <div
        className="md:hidden sticky top-[60px] z-20 bg-slate-950/95 backdrop-blur border-b border-white/10 overflow-x-auto no-scrollbar"
        data-testid="demo-mobile-tabstrip"
      >
        <div className="flex items-center gap-1.5 px-3 py-2 min-w-max">
          {ALL_TABS.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => handleTabChange(t.id)}
                className={`inline-flex items-center gap-1.5 px-3.5 h-11 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md shadow-violet-600/40'
                    : 'bg-white/5 text-slate-300 border border-white/10 active:bg-white/10'
                }`}
                data-testid={`demo-tabstrip-${t.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Slide-out drawer (mobile/tablet) */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="demo-drawer">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-[82%] max-w-sm bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 border-r border-white/[0.06] flex flex-col animate-slideIn shadow-2xl">
            <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BrandLogo size={36} />
                <div>
                  <div className="font-bold text-white text-base">Law Agency 360</div>
                  <div className="text-[11px] text-slate-500">Demo Legal Firm</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-white inline-flex items-center justify-center"
                aria-label="Close menu"
                data-testid="demo-drawer-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-4 space-y-1.5">
              {coreNav.map(it => {
                const Icon = it.icon;
                const isActive = activeTab === it.id;
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => handleTabChange(it.id)}
                    className={`w-full min-h-[44px] flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${
                      isActive
                        ? 'bg-gradient-to-r from-violet-500/25 to-indigo-500/15 text-white border border-violet-500/30'
                        : 'text-slate-300 active:bg-white/5'
                    }`}
                    data-testid={`demo-drawer-${it.id}`}
                  >
                    <Icon className="w-[18px] h-[18px]" />
                    {it.label}
                  </button>
                );
              })}
              <div className="pt-3 mt-1">
                <div className="flex items-center gap-2 px-2 mb-1.5">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-[10px] font-semibold tracking-[0.15em] text-slate-500 uppercase">Add-ons</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
                {addOnNav.map(it => {
                  const Icon = it.icon;
                  const isActive = activeTab === it.id;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => handleTabChange(it.id)}
                      className={`w-full min-h-[44px] flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${
                        isActive
                          ? 'bg-gradient-to-r from-violet-500/25 to-indigo-500/15 text-white border border-violet-500/30'
                          : 'text-slate-300 active:bg-white/5'
                      }`}
                      data-testid={`demo-drawer-${it.id}`}
                    >
                      <Icon className="w-[18px] h-[18px]" />
                      {it.label}
                    </button>
                  );
                })}
                <div className="flex items-center gap-2 px-2 mt-3 mb-1.5">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-violet-500/40 to-transparent" />
                  <span className="text-[10px] font-semibold tracking-[0.15em] text-violet-300/80 uppercase">Specialised</span>
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-violet-500/40 to-transparent" />
                </div>
                {specialNav.map(it => {
                  const Icon = it.icon;
                  const isActive = activeTab === it.id;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => handleTabChange(it.id)}
                      className={`w-full min-h-[44px] flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium border ${
                        isActive
                          ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white border-violet-400/60 shadow-lg shadow-violet-600/40'
                          : 'text-violet-100 bg-gradient-to-r from-violet-900/40 to-indigo-900/30 border-violet-500/25 active:from-violet-800/60'
                      }`}
                      data-testid={`demo-drawer-${it.id}`}
                    >
                      <Icon className="w-[18px] h-[18px]" />
                      {it.label}
                      <span className="ml-auto text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-violet-500/30 text-violet-100 border border-violet-400/30">Legal</span>
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>
        </div>
      )}

      <main className="p-4 sm:p-6 md:p-8 overflow-x-hidden relative max-w-full">
        <ActiveTab />
        <DemoChatBubble openSignal={chatOpenSignal} />
      </main>
      <DemoTour />
    </div>
  );
}
